import { TAGSvgIcon } from '@aspendental/shared-components-web';
import { Box, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { Filters } from '../DateAndTime.helpers';
import TimeButton from './TimeButton';
import { fadeCondition } from './TimeList.helpers';
import { OfficeSearchApiResponse, SlotsSearchResponseV4 } from '@src/lib/__generated/api';
import { trimTimeZoneOffset } from '@src/utils/trimTimeZoneOffset';
export default function TimeList({
  slots,
  location,
  filters
}: Readonly<{
  slots: SlotsSearchResponseV4[];
  location: OfficeSearchApiResponse;
  filters: Filters;
}>) {
  const container = useRef<HTMLElement>(null);
  const [scrollStart, setScrollStart] = useState(0);
  const [scrollEnd, setScrollEnd] = useState(false);
  const [scrollButton, setScrollButton] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const router = useRouter();
  const {
    locale
  } = router;
  const slide = (shift: number) => {
    if (!container.current) {
      return;
    }
    container.current.scrollLeft += shift;
    setScrollStart(scrollStart + shift);
    if (Math.floor(container.current.scrollWidth - container.current.scrollLeft) <= container.current.offsetWidth) {
      setScrollEnd(true);
    } else {
      setScrollEnd(false);
    }
  };
  const scrollCheck = () => {
    if (!container.current) return;
    setScrollStart(container.current.scrollLeft);
    if (Math.floor(container.current.scrollWidth - container.current.scrollLeft) <= container.current.offsetWidth) {
      setScrollEnd(true);
    } else {
      setScrollEnd(false);
    }
  };
  const isOverflowActive = () => {
    if (!container.current) {
      setScrollButton(true);
    } else {
      const isOverflow = container.current.offsetHeight < container.current.scrollHeight || container.current.offsetWidth < container.current.scrollWidth;
      setScrollButton(isOverflow);
    }
  };
  useEffect(() => {
    isOverflowActive();
  }, [slots, filters]);
  const rowTitle = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  }).format(new Date(trimTimeZoneOffset(slots[0]?.time) ?? ''));
  const timeButtonIdBase = `${location.facilityCode}_timeslot`;
  return <>
			<Typography variant="bodySmallBook" color="text.primary" pl={isSmallScreen ? 0 : 4} data-sentry-element="Typography" data-sentry-source-file="TimeList.tsx">
				{rowTitle}
			</Typography>
			<Box sx={{
      display: 'flex'
    }} mb={2} data-sentry-element="Box" data-sentry-source-file="TimeList.tsx">
				{scrollButton ? <IconButton color="secondary" sx={{
        padding: 0,
        borderRadius: 5,
        display: {
          xs: 'none',
          sm: 'block'
        }
      }} disabled={scrollStart === 0} onClick={() => slide(-200)} aria-label="scroll right to view later times">
						<TAGSvgIcon icon="ChevronLeftFunc" width="2rem" height="2rem" />
					</IconButton> : null}
				<Box ref={container} onScroll={scrollCheck} className="container" sx={{
        paddingLeft: scrollButton || isSmallScreen ? 0 : '2rem',
        paddingRight: '1rem',
        cursor: 'grab',
        overflow: 'hidden',
        scrollBehavior: 'smooth',
        overflowX: 'auto',
        '&::-webkit-scrollbar ': {
          background: 'transparent',
          WebkitAppearance: 'none',
          width: 0,
          height: 0
        },
        maskImage: fadeCondition({
          scrollButton,
          scrollStart,
          scrollEnd
        }),
        webkitMaskIimage: fadeCondition({
          scrollButton,
          scrollStart,
          scrollEnd
        }),
        display: 'flex'
      }} data-sentry-element="Box" data-sentry-source-file="TimeList.tsx">
					<Stack direction="row" spacing={2} m={0.25} data-sentry-element="Stack" data-sentry-source-file="TimeList.tsx">
						{slots.map(slot => {
            const appointmentTime = new Date(slot?.time ?? '');
            const appointmentLocalTime = appointmentTime.toLocaleTimeString();
            const timButtonIdTime = appointmentTime.toString().substring(0, 15) + '_' + appointmentLocalTime.replace(/:00/, '');
            return <TimeButton id={timeButtonIdBase + '_' + timButtonIdTime} key={slot.time} slot={slot} location={location} />;
          })}
					</Stack>
				</Box>
				{scrollButton ? <IconButton disabled={scrollEnd} onClick={() => slide(200)} color="secondary" sx={{
        borderRadius: 5,
        padding: 0,
        display: {
          xs: 'none',
          sm: 'block'
        }
      }} aria-label="scroll left to view earlier times">
						<TAGSvgIcon icon="ChevronRightFunc" height="2rem" width="2rem" />
					</IconButton> : null}
			</Box>
		</>;
}